import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FadeUp, SectionTag } from "../components/site/Reveal";
import WorkflowPipeline from "../components/site/WorkflowPipeline";

const PHASES = [
  {
    phase: "Phase 01", title: "Audit & Foundation", weeks: "Weeks 1–2",
    desc: "We map every way work enters your business and plug the leaks at the front door: one lead intake, one lead log, and quotes built from real numbers.",
    systems: ["Lead Capture", "Estimates"],
  },
  {
    phase: "Phase 02", title: "The Core Engine", weeks: "Weeks 3–5",
    desc: "The middle of the machine: a calendar that runs the week, customer records worth keeping, and same-day invoicing as a habit.",
    systems: ["Scheduling", "Customer Records", "Invoicing"],
  },
  {
    phase: "Phase 03", title: "Money & Momentum", weeks: "Weeks 6–7",
    desc: "Digital payments with a weekly money routine, then the follow-up engine that turns finished jobs into reviews, rebooks, and referrals.",
    systems: ["Payments", "Follow-Up"],
  },
];

export default function HowItWorks() {
  return (
    <div data-testid="how-it-works-page" className="bg-navy pt-16">
      <section className="blueprint-grid grain">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-28">
          <FadeUp>
            <SectionTag dark>Process // Three Phases</SectionTag>
            <h1 className="font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.03] mt-6 max-w-4xl">
              Guided, not done-for-you. <span className="text-safety">That's the point.</span>
            </h1>
            <p className="text-slate-300 text-lg leading-relaxed mt-6 max-w-2xl">
              Each week: one module, one working session, one artifact installed in your
              business. Coaching and accountability keep it moving — but your hands build
              it, so your hands can run it.
            </p>
          </FadeUp>
        </div>
      </section>

      <section className="bg-bone text-navy blueprint-grid-light">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
          <div className="grid lg:grid-cols-3 gap-px bg-slate-300 border border-slate-300">
            {PHASES.map((p, i) => (
              <FadeUp key={p.phase} delay={i * 0.12} className="h-full">
                <div className="bg-bone p-8 h-full flex flex-col" data-testid={`phase-card-${i + 1}`}>
                  <p className="font-mono text-[11px] tracking-[0.2em] text-safety uppercase">[{p.phase}]</p>
                  <h3 className="font-display font-extrabold text-2xl mt-3">{p.title}</h3>
                  <p className="font-mono text-[11px] text-slate-500 mt-1 uppercase tracking-widest">{p.weeks}</p>
                  <p className="text-slate-600 text-sm leading-relaxed mt-4 flex-1">{p.desc}</p>
                  <div className="mt-6 pt-4 border-t border-slate-300 flex flex-wrap gap-2">
                    {p.systems.map((s) => (
                      <span key={s} className="font-mono text-[10px] uppercase tracking-wider bg-navy text-white px-2.5 py-1">{s}</span>
                    ))}
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>

          <div className="mt-24">
            <FadeUp className="max-w-3xl mb-12">
              <SectionTag>Sandbox // Try the flow</SectionTag>
              <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl mt-5">
                The pipeline you'll build, node by node.
              </h2>
            </FadeUp>
            <FadeUp delay={0.15}>
              <WorkflowPipeline dark={false} />
            </FadeUp>
          </div>

          <FadeUp className="mt-24 text-center">
            <h2 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl">
              Ready to build yours?
            </h2>
            <Link
              to="/apply"
              data-testid="hiw-apply-btn"
              className="inline-flex items-center gap-2 bg-safety hover:bg-safety-hover text-white font-semibold px-10 py-4 mt-8 transition-colors"
            >
              Apply for the Blueprint <ArrowRight size={17} />
            </Link>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}
